import React from 'react'
import { PLACEHOLDER } from '../../group_leader/helpers/value'
import PropTypes from 'prop-types'
import { DataTable, PlayerIdentity } from '../../group_leader/components/common'
import { makeT } from '../../group_leader/helpers/i18n'
import { buildSlots, playerFullName } from '../../group_leader/helpers/player'
import { displayTime } from '../../group_leader/helpers/time'
import ResultsCard from './ResultsCard'

const t = makeT('group_leader.event_results.tee_sheet')

const DEFAULT_GROUP_SIZE = 4

// The configured group size, never below the largest placed group (blinds hold a seat), so no one is truncated.
const columnCount = (groups, groupSize) => {
  const placedMax = Math.max(...groups.map((group) =>
    (group.attributes?.players || []).length + (group.attributes?.blinds || []).length))
  const configured = parseInt(groupSize, 10)

  return Number.isNaN(configured)
    ? Math.max(placedMax, DEFAULT_GROUP_SIZE)
    : Math.max(configured, placedMax)
}

// A blind draw is shown bracketed, as the wizard shows it.
const seatName = (seat) => (seat._type === 'blind' ? `[${playerFullName(seat)}]` : playerFullName(seat))

// `groups` holds only the rows with somebody in them.
const TeeSheetCard = ({ groups, groupSize, hourFormat, isWalkon = false }) => {
  if (groups.length === 0) { return null }

  // A walk-on round has one single-seat foursome per golfer, so one golfer column.
  const size = isWalkon ? 1 : columnCount(groups, groupSize)
  // Walk-on has no tee times, but a round switched to walk-on keeps any real ones.
  const showTeeTimes = !isWalkon || groups.some((group) => group.attributes?.tee_time)
  const columns = [
    { key: 'ggid', label: t('col_ggid'), className: 'gl-event-results-ts-col-ggid' },
    { key: 'hole', label: t('col_hole'), className: 'gl-event-results-ts-col-hole' },
    ...(showTeeTimes
      ? [ { key: 'time', label: t('col_time'), className: 'gl-event-results-ts-col-time' } ]
      : []),
    ...Array.from({ length: size }, (_, index) => ({
      key: `seat-${index}`,
      label: t('col_player', { n: index + 1 }),
      seatIndex: index,
    })),
  ]

  // Seats resolved once per group, not once per cell; blinds come separately from the players.
  const seatsByRow = new Map(
    groups.map((group) => [ group.id, buildSlots(group.attributes?.players || [], group.attributes?.blinds || [], size) ]),
  )

  const renderCell = (group, column) => {
    const attrs = group.attributes || {}

    switch (column.key) {
      case 'ggid':
        return <span className="gl-event-results-ts-ggid">{attrs.foursome_ggid || PLACEHOLDER}</span>
      case 'hole':
        return <span className="gl-event-results-ts-hole">{attrs.hole || PLACEHOLDER}</span>
      case 'time':
        return <span className="gl-event-results-ts-time">{displayTime(attrs.tee_time, hourFormat) || PLACEHOLDER}</span>
      default: {
        const player = seatsByRow.get(group.id)?.[column.seatIndex]
        if (!player) { return null }
        return (
          <div className="gl-event-results-ts-slot">
            <PlayerIdentity name={seatName(player)} firstName={player.first_name} lastName={player.last_name} handicapIndex={player.handicap_index}/>
          </div>
        )
      }
    }
  }

  return (
    <ResultsCard
      title={t('title')}
      subtitle={isWalkon
        ? t('golfers', { count: groups.length })
        : t('groups', { count: groups.length })}
      >
      <DataTable
        className="gl-event-results-ts-table"
        columns={columns}
        rows={groups}
        rowKey={(group) => group.id}
        renderCell={renderCell}
        ariaLabel={t('aria_label')}
        renderMobileRow={(group) => {
          const attrs = group.attributes || {}
          const seated = seatsByRow.get(group.id).filter(Boolean)
          return (
            <div className="gl-event-results-ts-mobile-card">
              <div className="gl-event-results-ts-mobile-hd">
                {attrs.foursome_ggid && (
                  <span className="gl-event-results-ts-mobile-ggid">{attrs.foursome_ggid}</span>
                )}
                {attrs.hole && (
                  <span className="gl-event-results-ts-mobile-meta">
                    {t('hole_mobile', { n: attrs.hole })}
                  </span>
                )}
                {displayTime(attrs.tee_time, hourFormat) && (
                  <span className="gl-event-results-ts-mobile-meta">{displayTime(attrs.tee_time, hourFormat)}</span>
                )}
              </div>
              {seated.map((player, index) => (
                <div
                  key={player.player_round_id || index}
                  className="gl-event-results-ts-mobile-player"
                  >
                  <PlayerIdentity name={seatName(player)} firstName={player.first_name} lastName={player.last_name} handicapIndex={player.handicap_index}/>
                </div>
              ))}
            </div>
          )
        }}
        />
    </ResultsCard>
  )
}

TeeSheetCard.propTypes = {
  groups: PropTypes.array.isRequired,
  groupSize: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
  // The customer's 12/24-hour setting, as the live bar already honours.
  hourFormat: PropTypes.string,
  // Walk-on / no tee sheet: one golfer per foursome, so render a single golfer column.
  isWalkon: PropTypes.bool,
}

export default TeeSheetCard
